/**
 * Modal Layer Manager
 * 
 * Centralized modal state management that handles:
 * - Modal hierarchy and z-index management
 * - Body scroll lock coordination
 * - Modal interaction rules
 * - Clean separation of concerns
 */

// Modal layer definitions with clear hierarchy
export const MODAL_LAYERS = {
  // Base layer - page content
  PAGE: 0,
  
  // Evidence modals - low priority, can be covered by minimized results
  EVIDENCE: 50,
  
  // Minimized modals - medium priority, always accessible
  MINIMIZED: 100,
  
  // Results modals - high priority, full-screen experience
  RESULTS: 15000,
  
  // Nested modals - highest priority, temporary overlays
  NESTED: 20000,
  
  // System modals - critical alerts, confirmations
  SYSTEM: 30000
};

// Modal interaction rules
export const MODAL_RULES = {
  // When evidence modal opens
  EVIDENCE_OPENS: {
    // Don't affect minimized results (they stay minimized and visible above evidence)
    minimizedResults: 'IGNORE',
    // Close any open results modals
    openResults: 'CLOSE',
    // Close other evidence modals
    otherEvidence: 'CLOSE'
  },
  
  // When evidence modal closes
  EVIDENCE_CLOSES: {
    // Don't affect minimized results (they should persist)
    minimizedResults: 'IGNORE',
    // Don't affect other modals
    default: 'IGNORE'
  },
  
  // When results modal opens
  RESULTS_OPENS: {
    // Don't affect minimized results (allow multiple minimized)
    minimizedResults: 'IGNORE',
    // Allow evidence modals to coexist (for "Results" button workflow)
    evidence: 'IGNORE',
    // Allow other results modals to coexist (for minimized + new results)
    otherResults: 'IGNORE'
  },
  
  // When results modal minimizes
  RESULTS_MINIMIZES: {
    // Allow page interaction
    pageInteraction: 'ENABLE',
    // Don't affect other modals
    otherModals: 'IGNORE'
  }
};

class ModalLayerManager {
  constructor() {
    this.activeModals = new Map(); // modalId -> modalInfo
    this.scrollPosition = 0;
    this.bodyLocked = false;
    this.listeners = new Set();
  }

  /**
   * Register a modal with the manager
   */
  registerModal(modalId, type, layer, options = {}) {
    const modalInfo = {
      id: modalId,
      type,
      layer,
      isMinimized: false,
      timestamp: Date.now(),
      ...options
    };

    this.activeModals.set(modalId, modalInfo);
    this.applyModalRules(modalId, 'OPEN');
    this.updateBodyLock();
    this.notifyListeners({ action: 'REGISTER', modalId, modalInfo });

    console.log(`📝 Registered modal: ${modalId} (${type}, layer: ${layer})`);
    return modalInfo;
  }

  /**
   * Unregister a modal
   */
  unregisterModal(modalId) {
    const modalInfo = this.activeModals.get(modalId);
    if (!modalInfo) return;

    console.log(`🗑️ Unregistering modal: ${modalId}`, {
      type: modalInfo.type,
      isMinimized: modalInfo.isMinimized,
      remainingModals: Array.from(this.activeModals.keys()).filter(id => id !== modalId),
      bodyCurrentlyLocked: this.bodyLocked
    });

    this.activeModals.delete(modalId);
    this.applyModalRules(modalId, 'CLOSE');
    this.updateBodyLock();
    this.notifyListeners({ action: 'UNREGISTER', modalId, modalInfo });

    console.log(`✅ Modal unregistered: ${modalId}. Final state:`, {
      remainingModals: Array.from(this.activeModals.values()).map(m => ({
        modalId: m.modalId,
        type: m.type,
        isMinimized: m.isMinimized
      })),
      bodyLocked: this.bodyLocked,
      bodyHasClass: document.body.classList.contains('modal-body-locked'),
      bodyStyle: document.body.style.cssText
    });
  }

  /**
   * Update modal state (e.g., minimize/restore)
   */
  updateModal(modalId, updates) {
    const modalInfo = this.activeModals.get(modalId);
    if (!modalInfo) return;

    const oldState = { ...modalInfo };
    Object.assign(modalInfo, updates);

    // Handle minimize/restore
    if ('isMinimized' in updates) {
      this.applyModalRules(modalId, updates.isMinimized ? 'MINIMIZE' : 'RESTORE');
      this.updateBodyLock();
    }

    this.notifyListeners({ action: 'UPDATE', modalId, oldState, newState: modalInfo });
    console.log(`🔄 Updated modal: ${modalId}`, updates);
  }

  /**
   * Apply interaction rules when modal state changes
   */
  applyModalRules(modalId, action) {
    const modalInfo = this.activeModals.get(modalId);
    if (!modalInfo) return;

    const rules = this.getRulesForModal(modalInfo.type, action);
    
    console.log(`🔄 Applying ${action} rules for ${modalInfo.type} modal:`, {
      modalId,
      rules,
      otherModals: Array.from(this.activeModals.keys()).filter(id => id !== modalId),
      activeModalTypes: Array.from(this.activeModals.values()).map(m => m.type)
    });
    
    // Apply rules to other modals
    for (const [otherId, otherModal] of this.activeModals) {
      if (otherId === modalId) continue;

      const rule = rules[otherModal.type] || rules.default;
      console.log(`📋 Applying rule "${rule}" to modal ${otherId} (${otherModal.type})`);
      this.applyRuleToModal(otherId, rule, modalInfo);
    }
  }

  /**
   * Get interaction rules for a modal type and action
   */
  getRulesForModal(modalType, action) {
    // Evidence modal rules
    if (modalType === 'evidence' && action === 'OPEN') {
      return {
        results: 'IGNORE', // Allow results modals to coexist (for "Results" button)
        evidence: 'CLOSE', // Close other evidence modals
        default: 'IGNORE'
      };
    }
    
    if (modalType === 'evidence' && action === 'CLOSE') {
      return {
        results: 'IGNORE', // Don't affect results modals when evidence closes
        default: 'IGNORE'
      };
    }

    // Results modal rules
    if (modalType === 'results' && action === 'OPEN') {
      return {
        evidence: 'IGNORE', // Allow evidence modals to coexist (for "Results" button workflow)
        results: 'IGNORE', // Allow multiple results modals to coexist (for minimized + new results)
        default: 'IGNORE'
      };
    }

    return { default: 'IGNORE' };
  }

  /**
   * Apply a rule to a specific modal
   */
  applyRuleToModal(modalId, rule, triggeringModal) {
    if (rule === 'IGNORE') return;

    const modalInfo = this.activeModals.get(modalId);
    if (!modalInfo) return;

    if (rule === 'CLOSE') {
      // Request modal to close
      this.notifyListeners({ 
        action: 'CLOSE_REQUEST', 
        modalId, 
        reason: 'MODAL_RULE',
        triggeringModal: triggeringModal?.type || 'unknown'
      });
    }
  }

  /**
   * Manage body scroll lock based on active modals
   */
  updateBodyLock() {
    const shouldLock = this.shouldLockBody();

    if (shouldLock && !this.bodyLocked) {
      // Lock body scroll
      this.scrollPosition = window.pageYOffset;
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.classList.add('modal-body-locked');
      this.bodyLocked = true;
      console.log('🔒 ModalLayerManager: Body scroll locked at position:', this.scrollPosition);
    } else if (!shouldLock && this.bodyLocked) {
      // Unlock body scroll
      document.body.classList.remove('modal-body-locked');
      document.body.style.top = '';
      document.body.style.position = ''; // Ensure position is reset
      window.scrollTo(0, this.scrollPosition);
      this.bodyLocked = false;
      console.log('🔓 ModalLayerManager: Body scroll unlocked, restored to:', this.scrollPosition);
      
      // Debug: Check if body is properly reset
      setTimeout(() => {
        console.log('🔍 Body state after unlock:', {
          hasModalClass: document.body.classList.contains('modal-body-locked'),
          position: document.body.style.position,
          top: document.body.style.top,
          overflow: document.body.style.overflow
        });
        
        // Test if page is clickable
        const testClickHandler = (e) => {
          console.log('✅ Page is clickable after modal close:', e.target.tagName);
          document.removeEventListener('click', testClickHandler);
        };
        document.addEventListener('click', testClickHandler, { once: true });
        console.log('🔍 Added test click handler - click anywhere to verify page is interactive');
      }, 100);
    }
  }

  /**
   * Determine if body should be locked based on active modals
   */
  shouldLockBody() {
    // Lock if there are any non-minimized modals above the page layer
    // This includes evidence modals (50), results modals (15000), etc.
    for (const modal of this.activeModals.values()) {
      if (!modal.isMinimized && modal.layer > MODAL_LAYERS.PAGE) {
        console.log('🔒 Body should be locked due to modal:', {
          modalId: modal.modalId,
          type: modal.type,
          layer: modal.layer,
          isMinimized: modal.isMinimized
        });
        return true;
      }
    }
    
    console.log('🔓 Body should NOT be locked. Active modals:', 
      Array.from(this.activeModals.values()).map(m => ({
        modalId: m.modalId,
        type: m.type,
        layer: m.layer,
        isMinimized: m.isMinimized
      }))
    );
    return false;
  }

  /**
   * Get the highest z-index for a layer
   */
  getZIndexForLayer(layer) {
    let maxZIndex = layer;
    
    // Add small increments for modals in the same layer
    for (const modal of this.activeModals.values()) {
      if (modal.layer === layer) {
        maxZIndex += 1;
      }
    }
    
    return maxZIndex;
  }

  /**
   * Subscribe to modal events
   */
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Notify all listeners of modal events
   */
  notifyListeners(event) {
    // Notify registered listeners
    this.listeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('Modal listener error:', error);
      }
    });
    
    // Also dispatch global events for components not yet integrated with ModalLayerManager
    const globalEvent = new CustomEvent('modalLayerEvent', { detail: event });
    window.dispatchEvent(globalEvent);
  }

  /**
   * Check if any modals of a specific type are currently active
   */
  hasModalType(modalType) {
    for (const [, modalInfo] of this.activeModals) {
      if (modalInfo.type === modalType) {
        return true;
      }
    }
    return false;
  }

  /**
   * Get current modal state for debugging
   */
  getState() {
    return {
      activeModals: Array.from(this.activeModals.entries()),
      bodyLocked: this.bodyLocked,
      scrollPosition: this.scrollPosition
    };
  }
}

// Singleton instance
export const modalLayerManager = new ModalLayerManager();

// Make it globally available for legacy components
window.modalLayerManager = modalLayerManager;

// CSS class for body lock
const style = document.createElement('style');
style.textContent = `
  body.modal-body-locked {
    overflow: hidden !important;
    position: fixed;
    width: 100%;
    height: 100%;
  }
`;
document.head.appendChild(style);
